/**
 * OrchestrateSkillEffect — Specter phantom-path active: mass false alert.
 *
 * Plants a false alert at a target location. All enemies whose detection range
 * overlaps that position redirect investigation there for 6 seconds. The room
 * turns its back at once — no fighting, just misdirection.
 *
 * Does not require Stealth. Long cooldown (2000ms). XP-based fizzle chance.
 * Maximum placement range: 8 tiles (design-decisions.md).
 *
 * Unlike Phantom/Doppelganger, Orchestrate does not spawn a summon entity.
 * It broadcasts a false alert signal directly to the AI layer. isActive()
 * returns true during the investigation window so that the Conductor passive
 * can detect active illusions for XP bonuses.
 *
 * Upgrade hooks (wired here, values resolved by upgrade tasks):
 * - Credible (allyPullChance): per-enemy roll to pull a nearby ally.
 * - Planted (patrolReturnDelayMs): extra patrol-resume delay after investigation.
 * - Planted (traceReadDurationMs): footprint overlay duration (visual only).
 * - Redirect (postInvestigationSpeedReduction): post-investigation slow.
 * - Redirect (patrolResumeDelayMs): patrol-resume delay from tree passive.
 *
 * Implements SkillEffect directly — same DI architecture as Phantom/Doppelganger.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see specter-spec.md § Orchestrate
 * @see design-decisions.md § Orchestrate Targeting Range: 8 Tiles
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OrchestrateDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    hasEffect(skillId: string, key: string): boolean;
    getTreePassiveEffect(treeName: string, key: string, aggregation: 'sum' | 'max'): number;
  };
  readonly targeting: {
    shouldFizzle(): boolean;
    getTargetPosition(): { x: number; y: number };
  };
  readonly ai: {
    getEnemiesInDetectionRange(targetX: number, targetY: number): string[];
    redirectToInvestigate(enemyId: string, targetX: number, targetY: number): void;
    getNearbyNonInvestigating(sourceEnemyId: string, rangeTiles: number): string[];
  };
  readonly statusEffects: {
    apply(defId: string, opts: {
      sourceSkillId: string;
      durationMs: number;
      targetEntityId: string;
    }): void;
  };
  readonly rng: {
    roll(): number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface OrchestrateConfig {
  readonly cooldownMs: number;
  readonly investigationDurationMs: number;
  readonly credibleCommunicationRangeTiles: number;
}

const DEFAULT_CONFIG: OrchestrateConfig = {
  cooldownMs: 2000,                           // long cooldown tier
  investigationDurationMs: 6000,              // 6-second investigation window
  credibleCommunicationRangeTiles: 4,         // spec suggestion for Credible pull range
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OrchestrateSkillEffect implements SkillEffect {
  readonly skillId = 'orchestrate';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: OrchestrateDeps;
  private readonly config: OrchestrateConfig;

  private _cooldownRemaining = 0;
  private _investigationTimerMs = 0;

  // Activation-result state (observable by scenarios and game layer)
  private _activationCount = 0;
  private _fizzled = false;
  private _redirectedEnemyIds: string[] = [];
  private _crediblePulledIds: string[] = [];
  private _investigationEndedEnemyIds: string[] = [];
  private _targetX = 0;
  private _targetY = 0;

  // Resolved upgrade values
  private _resolvedAllyPullChance = 0;
  private _resolvedRedirectSpeedReduction = 0;
  private _resolvedPatrolResumeDelayMs = 0;
  private _resolvedPlantedDelayMs = 0;
  private _resolvedTraceReadDurationMs = 0;

  constructor(deps: OrchestrateDeps, config: Partial<OrchestrateConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get activationCount(): number { return this._activationCount; }
  get fizzled(): boolean { return this._fizzled; }
  get redirectedEnemyIds(): readonly string[] { return this._redirectedEnemyIds; }
  get crediblePulledIds(): readonly string[] { return this._crediblePulledIds; }
  get investigationEndedEnemyIds(): readonly string[] { return this._investigationEndedEnemyIds; }
  get investigationTimerMs(): number { return this._investigationTimerMs; }
  get targetX(): number { return this._targetX; }
  get targetY(): number { return this._targetY; }
  get resolvedAllyPullChance(): number { return this._resolvedAllyPullChance; }
  get resolvedRedirectSpeedReduction(): number { return this._resolvedRedirectSpeedReduction; }
  get resolvedPatrolResumeDelayMs(): number { return this._resolvedPatrolResumeDelayMs; }
  get resolvedPlantedDelayMs(): number { return this._resolvedPlantedDelayMs; }
  get resolvedTraceReadDurationMs(): number { return this._resolvedTraceReadDurationMs; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Fizzle check — consumes cooldown but does not redirect
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      this._cooldownRemaining = this.config.cooldownMs;
      return false;
    }

    // Get target position
    const target = this.deps.targeting.getTargetPosition();
    this._targetX = target.x;
    this._targetY = target.y;

    // Resolve upgrade effects
    // Credible: ally pull chance (scope: 'orchestrate')
    this._resolvedAllyPullChance = this.deps.resolver.getAdditiveEffect(
      'orchestrate', 'allyPullChance',
    );

    // Planted: extra patrol delay + trace overlay (scope: 'orchestrate')
    this._resolvedPlantedDelayMs = this.deps.resolver.getAdditiveEffect(
      'orchestrate', 'patrolReturnDelayMs',
    );
    this._resolvedTraceReadDurationMs = this.deps.resolver.getAdditiveEffect(
      'orchestrate', 'traceReadDurationMs',
    );

    // Redirect (tree passive): post-investigation slow + patrol delay
    this._resolvedRedirectSpeedReduction = this.deps.resolver.getTreePassiveEffect(
      'specter', 'postInvestigationSpeedReduction', 'sum',
    );
    this._resolvedPatrolResumeDelayMs = this.deps.resolver.getTreePassiveEffect(
      'specter', 'patrolResumeDelayMs', 'sum',
    );

    // Get enemies in detection range and redirect them
    const enemies = this.deps.ai.getEnemiesInDetectionRange(target.x, target.y);
    this._redirectedEnemyIds = [...enemies];
    this._crediblePulledIds = [];
    this._investigationEndedEnemyIds = [];

    for (const enemyId of enemies) {
      this.deps.ai.redirectToInvestigate(enemyId, target.x, target.y);
    }

    // Credible: per-enemy roll to pull a nearby non-investigating ally
    if (this._resolvedAllyPullChance > 0) {
      const alreadyRedirected = new Set(this._redirectedEnemyIds);
      for (const enemyId of enemies) {
        if (this.deps.rng.roll() < this._resolvedAllyPullChance) {
          const nearby = this.deps.ai.getNearbyNonInvestigating(
            enemyId, this.config.credibleCommunicationRangeTiles,
          );
          // Pull the first available ally not already redirected (no recursive chaining)
          for (const allyId of nearby) {
            if (!alreadyRedirected.has(allyId)) {
              this.deps.ai.redirectToInvestigate(allyId, target.x, target.y);
              this._crediblePulledIds.push(allyId);
              alreadyRedirected.add(allyId);
              break;
            }
          }
        }
      }
    }

    // Start investigation timer
    this._investigationTimerMs = this.config.investigationDurationMs;

    // Start cooldown
    this._cooldownRemaining = this.config.cooldownMs;
    this._activationCount++;

    return true;
  }

  deactivate(): void {
    // Instant skill with investigation window — no toggle deactivation
  }

  isActive(): boolean {
    return this._investigationTimerMs > 0;
  }

  update(delta: number): void {
    // Tick cooldown
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }

    // Tick investigation timer
    if (this._investigationTimerMs > 0) {
      this._investigationTimerMs = Math.max(0, this._investigationTimerMs - delta);
      if (this._investigationTimerMs <= 0) {
        this.onInvestigationEnd();
      }
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._investigationTimerMs = 0;
  }

  // --- Private ---

  private onInvestigationEnd(): void {
    const allRedirected = [
      ...this._redirectedEnemyIds,
      ...this._crediblePulledIds,
    ];
    this._investigationEndedEnemyIds = allRedirected;

    // Apply Redirect post-investigation slow (if Redirect unlocked)
    if (this._resolvedRedirectSpeedReduction > 0) {
      // Total delay = Redirect's base delay + Planted's bonus delay
      const totalDelay = this._resolvedPatrolResumeDelayMs + this._resolvedPlantedDelayMs;
      const slowDuration = totalDelay > 0 ? totalDelay : 3000;

      for (const enemyId of allRedirected) {
        this.deps.statusEffects.apply('post_investigation_slow', {
          sourceSkillId: 'orchestrate',
          durationMs: slowDuration,
          targetEntityId: enemyId,
        });
      }
    }
  }
}
